import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  deleteField,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { generateAssignmentId } from '../utils/idGenerator';
import { optimizedAssignmentServiceV3 } from './optimizedAssignmentServiceV3';
import { 
  RoutineAssignment, 
  RoutineAssignmentFormData,
  RoutineAssignmentDateStatus,
  RoutineAssignmentStudentStatus,
  AssignmentStatusValue,
  RoutineAssignmentCreateOptions,
  RoutineAssignmentUpdateOptions
} from '../types/alarm';
import { RoutineAssignmentConsolidatedService } from './routineAssignmentConsolidatedService';
import { normalizeMemberGroup, withMemberGroup } from '../utils/memberGroupPaths';
import { assignmentMemoService } from './assignmentMemoService';

/**
 * Firestore 데이터를 Date 객체로 변환하는 유틸리티 함수
 */

/**
 * Date 객체를 Firestore Timestamp로 변환하는 유틸리티 함수
 */
function dateToTimestamp(date: Date): Timestamp {
  return Timestamp.fromDate(date);
}

/**
 * 날짜를 YYYY-MM-DD 형식의 ID로 변환 (한국 시간대 고려)
 */
function dateToDateId(date: Date): string {
  const dateStr = date.getFullYear() + '-' + 
    String(date.getMonth() + 1).padStart(2, '0') + '-' + 
    String(date.getDate()).padStart(2, '0');
  return dateStr;
}

/**
 * 새로운 구조의 루틴 과제 서비스
 */
export const routineAssignmentService = {
  /**
   * undefined 값을 null로 변환하는 유틸리티 함수 (Firebase는 undefined를 지원하지 않음)
   */
  filterUndefinedValues<T extends Record<string, any>>(obj: T): Partial<T> {
    const filtered: Partial<T> = {};
    
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      
      // undefined만 null로 변환, 다른 값들은 모두 포함
      if (value === undefined) {
        (filtered as any)[key] = null;
      } else {
        (filtered as any)[key] = value;
      }
    });
    
    return filtered;
  },

  /**
   * 루틴 과제 생성 (통합 문서 방식)
   */
  async createRoutineAssignment(
    userId: string, 
    classId: string, 
    formData: RoutineAssignmentFormData,
    options: RoutineAssignmentCreateOptions = {},
    memberGroup?: string,
    assignmentId?: string
  ): Promise<RoutineAssignment> {
    try {
    const g = normalizeMemberGroup(memberGroup);
    const rootCollectionName = withMemberGroup('routineAssignmentStatuses', g);
    // undefined 값 필터링
    const cleanFormData = this.filterUndefinedValues(formData);
    
      console.log('새로운 구조: 루틴 과제 생성 시작');
    console.log('원본 formData:', formData);
    console.log('정리된 cleanFormData:', cleanFormData);
    
    const routineId = assignmentId || generateAssignmentId('routine');
    const routineAssignment: RoutineAssignment = {
      id: routineId, // RoutineAssignment 타입을 위해 유지하지만 저장하지 않음
      title: cleanFormData.title || '',
      ...(cleanFormData.description && { description: cleanFormData.description }),
      startDate: dateToTimestamp(cleanFormData.startDate || new Date()), // 시작일 추가
      routineDays: cleanFormData.routineDays || [],
      dueDate: dateToTimestamp(cleanFormData.dueDate || new Date()),
      basePoints: cleanFormData.basePoints || 10,
      assignedStudentIds: cleanFormData.assignedStudentIds || [],
      categoryId: cleanFormData.categoryId,
      rewardEnabled: cleanFormData.rewardEnabled ?? false,
      rewardMessage: cleanFormData.rewardMessage || '',
      rewardCoins: cleanFormData.rewardCoins ?? 0,
      rewardItemCardIds: cleanFormData.rewardItemCardIds || [],
      rewardCompletionRate: cleanFormData.rewardCompletionRate ?? 100,
      includeInIncompleteCheck: cleanFormData.includeInIncompleteCheck ?? false,
      customDates: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      isActive: true,
      classId
    };
    
    console.log('최종 routineAssignment:', routineAssignment);

     const routineRef = doc(db, 'users', userId, 'classes', classId, rootCollectionName, routineAssignment.id);
    
    // 상위 문서는 루틴 과제 ID 참조용으로만 사용 (최소한의 정보만 저장)
    await setDoc(routineRef, {
      assignmentId: routineId,
      title: routineAssignment.title, // 디버깅용으로 제목만 저장
      createdAt: routineAssignment.createdAt,
      updatedAt: routineAssignment.updatedAt
    });
    
    // 통합 문서 방식으로 날짜별 상태 생성
    if (options.generateRecords !== false) {
      const consolidatedService = new RoutineAssignmentConsolidatedService(userId, classId, g);
      await consolidatedService.createRoutineAssignment(routineAssignment, cleanFormData as RoutineAssignmentFormData);
    }
    
    console.log('통합 문서 방식: 루틴 과제 생성 완료');

    return routineAssignment;
    } catch (error) {
      console.error('새로운 구조: 루틴 과제 생성 실패:', error);
      throw error;
    }
  },

  /**
   * 루틴 과제 수정 (통합 문서 구조)
   */
  async updateRoutineAssignment(
    userId: string,
    classId: string,
    routineId: string,
    updates: Partial<RoutineAssignmentFormData>,
    options: RoutineAssignmentUpdateOptions = {},
    memberGroup?: string
  ): Promise<void> {
    try {
      // 통합 서비스 사용
      const consolidatedService = new RoutineAssignmentConsolidatedService(
        userId,
        classId,
        normalizeMemberGroup(memberGroup)
      );
      
      // RoutineAssignmentFormData 형태로 변환
      const formData: RoutineAssignmentFormData = {
        title: updates.title || '',
        description: updates.description || '',
        startDate: updates.startDate || new Date(),
        routineDays: updates.routineDays || [],
        dueDate: updates.dueDate || new Date(),
        basePoints: updates.basePoints || 0,
        assignedStudentIds: updates.assignedStudentIds || [],
        categoryId: updates.categoryId || 'routine',
        icon: updates.icon || '',
        notes: updates.notes || '',
        rewardEnabled: updates.rewardEnabled ?? false,
        rewardMessage: updates.rewardMessage || '',
        rewardCoins: updates.rewardCoins ?? 0,
        rewardItemCardIds: updates.rewardItemCardIds || [],
        rewardCompletionRate: updates.rewardCompletionRate ?? 100,
        includeInIncompleteCheck: updates.includeInIncompleteCheck ?? false
      };
      
      await consolidatedService.updateRoutineAssignment(routineId, formData);
      
      console.log('✅ 통합 서비스: 루틴 과제 수정 완료:', {
        routineId,
        title: formData.title,
        studentCount: formData.assignedStudentIds.length
      });
      
    } catch (error) {
      console.error('❌ 통합 서비스: 루틴 과제 수정 실패:', error);
      throw error;
    }
  },

  /**
   * 루틴 과제 삭제 (통합 문서 구조)
   */
  async deleteRoutineAssignment(
    userId: string,
    classId: string,
    routineId: string,
    memberGroup?: string
  ): Promise<void> {
    try {
      const g = normalizeMemberGroup(memberGroup);
      const rootCollectionName = withMemberGroup('routineAssignmentStatuses', g);
      const batch = writeBatch(db);
      
      // 루틴 과제 삭제
      const routineRef = doc(db, 'users', userId, 'classes', classId, rootCollectionName, routineId);
      batch.delete(routineRef);
      
      // 통합 문서 구조 삭제: routineAssignmentStatuses/{routineId}/DateAssignment/
      const consolidatedRef = collection(
        db, 
        'users', 
        userId, 
        'classes', 
        classId, 
        rootCollectionName, 
        routineId, 
        'DateAssignment'
      );
      
      const consolidatedSnap = await getDocs(consolidatedRef);
      
      // 모든 통합 문서 삭제 (DateAssignment-1, DateAssignment-2, ...)
      consolidatedSnap.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // 기존 구조도 삭제 (혹시 남아있을 수 있는 경우)
      const routineStatusRef = doc(
        db, 
        'users', 
        userId, 
        'classes', 
        classId, 
        rootCollectionName, 
        routineId
      );
      
      const datesRef = collection(routineStatusRef, 'dates');
      const datesSnap = await getDocs(datesRef);
      
      // 각 날짜별로 students 컬렉션과 함께 삭제
      for (const dateDoc of datesSnap.docs) {
        const studentsRef = collection(dateDoc.ref, 'students');
        const studentsSnap = await getDocs(studentsRef);
        
        // 멤버별 문서 삭제
        studentsSnap.docs.forEach(studentDoc => {
          batch.delete(studentDoc.ref);
        });
        
        // 날짜별 문서 삭제
        batch.delete(dateDoc.ref);
      }
    
      await batch.commit();
      console.log('✅ 루틴 과제 및 통합 문서 삭제 완료:', {
        routineId,
        consolidatedDocsDeleted: consolidatedSnap.docs.length,
        dateDocsDeleted: datesSnap.docs.length
      });
    } catch (error) {
      console.error('❌ 루틴 과제 삭제 실패:', error);
      throw error;
    }
  },

  /**
   * 루틴 과제 목록 조회 (새로운 구조)
   */
  async loadRoutineAssignments(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<RoutineAssignment[]> {
    try {
      const g = normalizeMemberGroup(memberGroup);
      const rootCollectionName = withMemberGroup('routineAssignmentStatuses', g);
      // 통합 서비스 사용하여 DateAssignment-X 문서들에서 루틴 과제 목록 로드
      const consolidatedService = new RoutineAssignmentConsolidatedService(userId, classId, g);
      
      // 모든 루틴 과제 ID 찾기 (routineAssignmentStatuses 컬렉션에서)
      const routineRef = collection(db, 'users', userId, 'classes', classId, rootCollectionName);
      const routineSnap = await getDocs(routineRef);
      
      const routines: RoutineAssignment[] = [];
      
      // 각 루틴 과제에 대해 첫 번째 DateAssignment-X 문서에서 메타데이터 로드
      for (const doc of routineSnap.docs) {
        const routineId = doc.id;
        try {
          const consolidatedDocs = await consolidatedService.getAllConsolidatedDocuments(routineId);
          if (consolidatedDocs.length > 0) {
            const firstDoc = consolidatedDocs[0]; // 첫 번째 DateAssignment-X 문서에서 메타데이터 가져오기
            
            routines.push({
              id: routineId,
              title: firstDoc.title,
              description: firstDoc.description || '',
              startDate: new Date(firstDoc.startDate),
              dueDate: new Date(firstDoc.dueDate),
              basePoints: firstDoc.basePoints,
              assignedStudentIds: firstDoc.assignedStudentIds,
              routineDays: firstDoc.routineDays || [],
              icon: firstDoc.icon || '',
              notes: firstDoc.notes || '',
              isActive: firstDoc.isActive !== undefined ? firstDoc.isActive : true,
              categoryId: firstDoc.categoryId || 'routine',
              rewardEnabled: firstDoc.rewardEnabled ?? false,
              rewardMessage: firstDoc.rewardMessage || '',
              rewardCoins: firstDoc.rewardCoins ?? 0,
              rewardItemCardIds: firstDoc.rewardItemCardIds || [],
              rewardGrantedStudentIds: firstDoc.rewardGrantedStudentIds || [],
              rewardCompletionRate: firstDoc.rewardCompletionRate ?? 100,
              includeInIncompleteCheck: firstDoc.includeInIncompleteCheck ?? false,
              createdAt: firstDoc.createdAt,
              updatedAt: firstDoc.lastUpdated,
              customDates: [],
              classId: firstDoc.classId
            } as RoutineAssignment);
          }
        } catch (error) {
          console.warn(`루틴 과제 ${routineId} 로드 실패:`, error);
        }
      }
      
      console.log('✅ 통합 서비스: 루틴 과제 목록 조회 완료', { count: routines.length });
      return routines;
    } catch (error) {
      console.error('❌ 통합 서비스: 루틴 과제 목록 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 루틴 과제 상세 조회 (통합 문서 방식)
   */
  async loadRoutineAssignmentWithStatuses(
    userId: string,
    classId: string,
    routineId: string,
    memberGroup?: string
  ): Promise<{
    routine: RoutineAssignment;
    dateStatuses: RoutineAssignmentDateStatus[];
  }> {
    try {
      // 통합 문서 서비스 사용
      const consolidatedService = new RoutineAssignmentConsolidatedService(
        userId,
        classId,
        normalizeMemberGroup(memberGroup)
      );
      const result = await consolidatedService.loadRoutineAssignmentWithStatuses(routineId);
      
      if (!result.routine) {
        throw new Error('루틴 과제를 찾을 수 없습니다.');
      }
      
      const memoDoc = await assignmentMemoService.loadMemoDocument(userId, classId, memberGroup);

      // RoutineAssignmentDateStatus 형식으로 변환
      const dateStatuses: RoutineAssignmentDateStatus[] = result.dateStatuses.map(dateAssignment => {
        const students: Record<string, RoutineAssignmentStudentStatus> = {};
        
        // studentStatuses가 없으면 빈 배열로 처리
        const studentStatuses = dateAssignment.studentStatuses || [];
        studentStatuses.forEach((studentStatus: any) => {
          const submittedAt = studentStatus.submittedAt?.toDate ? 
                             studentStatus.submittedAt.toDate() : 
                             studentStatus.submittedAt;
          const assignmentKey = `${routineId}-${dateAssignment.date}`;
          const memoEntry = memoDoc.assignments?.[assignmentKey]?.[studentStatus.studentId];
          
          students[studentStatus.studentId] = {
            id: studentStatus.studentId,
            studentId: studentStatus.studentId,
            assignmentId: routineId,
            status: studentStatus.status,
            earnedPoints: studentStatus.points,
            memo: memoEntry?.memo || studentStatus.memo || '',
            submissionLink: studentStatus.submissionLink,
            submissionMessage: studentStatus.submissionMessage,
            submittedAt,
            updatedAt: studentStatus.updatedAt
          };
          
        });
        
        return {
          dateId: dateAssignment.date,
          routineAssignmentId: routineId,
          date: new Date(dateAssignment.date),
          isCustomDate: dateAssignment.isCustomDate,
          students,
          totalStudents: studentStatuses.length,
          completedStudents: studentStatuses.filter(s => s.status === 'confirmed').length,
          pendingStudents: studentStatuses.filter(s => s.status === 'pending').length,
          updatedAt: new Date()
        };
      });
      
      return {
        routine: result.routine,
        dateStatuses
      };
      
    } catch (error) {
      console.error('통합 문서 방식: 루틴 과제 상세 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 루틴 과제 상태기록 업데이트 (통합 문서 구조 사용)
   */
  async updateRoutineAssignmentStatus(
    userId: string,
    classId: string,
    routineId: string,
    dateId: string,
    studentId: string,
    statusData: Partial<RoutineAssignmentStudentStatus>,
    memberGroup?: string
  ): Promise<void> {
    try {
      console.log('🚀 updateRoutineAssignmentStatus 호출됨 (통합 문서 구조):', {
        userId,
        classId,
        routineId,
        dateId,
        studentId,
        statusData
      });
      
      // 통합 문서 서비스 사용
      const consolidatedService = new RoutineAssignmentConsolidatedService(userId, classId, normalizeMemberGroup(memberGroup));
      await assignmentMemoService.saveMemo(
        userId,
        classId,
        `${routineId}-${dateId}`,
        studentId,
        statusData.memo || '',
        undefined,
        undefined,
        memberGroup
      );
      await consolidatedService.updateStudentStatus(
        routineId, 
        dateId, 
        studentId, 
        statusData.status || 'pending',
        statusData.earnedPoints || 0,
        ''
      );
      
      console.log('✅ 통합 문서 구조: 루틴 과제 상태기록 업데이트 완료');
    } catch (error) {
      console.error('통합 문서 구조: 루틴 과제 상태기록 업데이트 실패:', error);
      throw error;
    }
  },

  /**
   * 날짜별 요약 정보 업데이트 (새로운 구조)
   */
  async updateDateStatusSummary(
    userId: string,
    classId: string,
    routineId: string,
    dateId: string
  ): Promise<void> {
    try {
      // 새로운 구조: routineAssignmentStatuses/{routineId}/dates/{dateId}/students/{studentId}
      const routineStatusRef = doc(
        db, 
        'users', 
        userId, 
        'classes', 
        classId, 
        'routineAssignmentStatuses', 
        routineId
      );
      
      const datesRef = collection(routineStatusRef, 'dates');
      const dateRef = doc(datesRef, dateId);
      const studentsRef = collection(dateRef, 'students');
      
      await getDocs(studentsRef);

      // 날짜별 요약 정보는 별도로 저장하지 않음 (새로운 구조에서는 students 컬렉션에서 계산)
      // 필요시 여기서 통계 정보를 별도 문서로 저장할 수 있음
      console.log('새로운 구조: 날짜별 요약 정보는 students 컬렉션에서 실시간 계산');
      
      console.log('새로운 구조: 날짜별 요약 정보 업데이트 완료');
    } catch (error) {
      console.error('새로운 구조: 날짜별 요약 정보 업데이트 실패:', error);
      throw error;
    }
  },

  /**
   * 루틴 날짜 계산 (새로운 구조)
   */
  calculateRoutineDates(routineDays: number[], dueDate: Date, startDate?: Date): Date[] {
    const dates: Date[] = [];
    
    // 시작일 설정: startDate가 있으면 사용, 없으면 과거 1년 전부터
    const actualStartDate = startDate || new Date(new Date().getFullYear() - 1, 0, 1);
    actualStartDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(dueDate);
    endDate.setHours(23, 59, 59, 999);
    
    const currentDate = new Date(actualStartDate);
    
    console.log('🔍 routineAssignmentService - calculateRoutineDates 시작:', {
      routineDays,
      startDate: actualStartDate.toISOString(),
      endDate: endDate.toISOString(),
      currentDate: currentDate.toISOString()
    });
    
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      if (routineDays.includes(dayOfWeek)) {
        dates.push(new Date(currentDate));
        console.log(`✅ routineAssignmentService - 루틴 날짜 추가: ${currentDate.toISOString().split('T')[0]} (dayOfWeek: ${dayOfWeek}, 요일: ${['일', '월', '화', '수', '목', '금', '토'][dayOfWeek]})`);
      } else {
        console.log(`❌ routineAssignmentService - 루틴 요일에 포함되지 않음: ${currentDate.toISOString().split('T')[0]} (dayOfWeek: ${dayOfWeek}, 요일: ${['일', '월', '화', '수', '목', '금', '토'][dayOfWeek]})`);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log('🔍 routineAssignmentService - calculateRoutineDates 완료:', {
      totalDates: dates.length,
      dates: dates.map(d => d.toISOString().split('T')[0])
    });
    
    return dates;
  },

  /**
   * 🗑️ 날짜별 과제 데이터 삭제 (새로운 구조)
   */
  async deleteDateAssignment(
    userId: string,
    classId: string,
    routineId: string,
    dateId: string
  ): Promise<void> {
    try {
      console.log('🗑️ deleteDateAssignment 호출됨:', { userId, classId, routineId, dateId });
      
      // 올바른 경로: users/{userId}/classes/{classId}/routineAssignmentStatuses/{routineId}/dates/{dateId}
      const routineStatusRef = doc(
        db, 
        'users', 
        userId, 
        'classes', 
        classId,  // 🎯 수정: routineId → classId
        'routineAssignmentStatuses', 
        routineId
      );
      
      const dateRef = doc(collection(routineStatusRef, 'dates'), dateId);
      
      console.log('🗑️ 삭제할 경로:', dateRef.path);
      
      // 해당 날짜 문서 삭제 (students 컬렉션도 함께 삭제됨)
      await deleteDoc(dateRef);
      
      console.log('✅ 날짜별 과제 데이터 삭제 완료:', dateId);
    } catch (error) {
      console.error('❌ 날짜별 과제 데이터 삭제 실패:', error);
      throw error;
    }
  },

  /**
   * 🗑️ 루틴 과제 특정 날짜 완전 삭제 (새로운 구조)
   * @param memberGroup - 멤버 그룹 (A/B/C 등). 현재 보고 있는 그룹 경로와 일치해야 삭제 가능.
   */
  async deleteRoutineAssignmentDate(
    userId: string,
    classId: string,
    routineId: string,
    dateId: string,
    memberGroup?: string
  ): Promise<void> {
    try {
      const g = normalizeMemberGroup(memberGroup);
      const rootCollectionName = withMemberGroup('routineAssignmentStatuses', g);
      const collectionPath = `users/${userId}/classes/${classId}/${rootCollectionName}/${routineId}/DateAssignment`;

      console.log('🗑️ deleteRoutineAssignmentDate 시작:', { userId, classId, routineId, dateId, memberGroup, rootCollectionName });
      
      const dateAssignmentSnapshot = await getDocs(collection(db, collectionPath));
      console.log(`🔍 DateAssignment 문서 수: ${dateAssignmentSnapshot.docs.length}`);
      
      let foundDocument = false;
      
      for (const docSnapshot of dateAssignmentSnapshot.docs) {
        const data = docSnapshot.data();
        const dateAssignmentsRaw = data.dateAssignments;
        
        // V2: 객체 형식 { [dateId]: {...} }
        if (dateAssignmentsRaw && typeof dateAssignmentsRaw === 'object' && !Array.isArray(dateAssignmentsRaw)) {
          if (!(dateId in dateAssignmentsRaw)) continue;
          
          const updated = { ...dateAssignmentsRaw };
          delete updated[dateId];
          
          const docRef = doc(db, collectionPath, docSnapshot.id);
          await updateDoc(docRef, {
            [`dateAssignments.${dateId}`]: deleteField(),
            lastUpdated: new Date(),
            totalSize: JSON.stringify(updated).length
          });
          
          console.log('✅ V2 형식 날짜 삭제 완료');
          foundDocument = true;
          break;
        }
        
        // V1: 배열 형식 [{ date, studentStatuses, ... }]
        const dateAssignments = Array.isArray(dateAssignmentsRaw) ? dateAssignmentsRaw : [];
        const dateIndex = dateAssignments.findIndex((da: any) => da.date === dateId);
        
        if (dateIndex !== -1) {
          const updatedDateAssignments = dateAssignments.filter((da: any) => da.date !== dateId);
          const docRef = doc(db, collectionPath, docSnapshot.id);
          await updateDoc(docRef, {
            dateAssignments: updatedDateAssignments,
            lastUpdated: new Date(),
            totalSize: JSON.stringify(updatedDateAssignments).length
          });
          
          console.log('✅ V1 형식 날짜 삭제 완료');
          foundDocument = true;
          break;
        }
      }
      
      if (!foundDocument) {
        console.log('⚠️ 삭제할 날짜를 찾을 수 없음:', { dateId, rootCollectionName });
        throw new Error(`날짜 ${dateId}를 찾을 수 없습니다. 현재 선택한 그룹(${g})의 경로에서 확인해 주세요.`);
      }
      
      RoutineAssignmentConsolidatedService.invalidateCacheForRoutine(userId, classId, memberGroup, routineId);
      console.log('✅ deleteRoutineAssignmentDate 완료');
    } catch (error) {
      console.error('❌ 루틴 과제 날짜 삭제 실패:', error);
      throw error;
    }
  },

  /**
   * 초기 날짜별 상태기록 생성 (새로운 구조)
   */
  generateInitialDateStatuses(
    routineId: string,
    dates: Date[],
    studentIds: string[]
  ): RoutineAssignmentDateStatus[] {
    const dateStatuses: RoutineAssignmentDateStatus[] = [];
    
    dates.forEach(date => {
      const students: Record<string, RoutineAssignmentStudentStatus> = {};
      
      studentIds.forEach(studentId => {
                          students[studentId] = {
          id: studentId, // studentId와 동일
          studentId,
          assignmentId: routineId, // 루틴 과제 ID
          status: 'pending',
          earnedPoints: 0,
          updatedAt: new Date()
          // confirmedAt, notes, memo는 undefined일 때 필드를 아예 포함하지 않음
        };
      });
      
      dateStatuses.push({
        dateId: dateToDateId(date),
        routineAssignmentId: routineId,
        date,
        students,
        totalStudents: studentIds.length,
        completedStudents: 0,
        pendingStudents: studentIds.length,
        updatedAt: new Date()
      });
    });
    
    return dateStatuses;
  },

  /**
   * 멤버 삭제 시 루틴 과제 관련 데이터 정리
   */
  async cleanupStudentRoutineAssignmentData(
    userId: string,
    classId: string,
    studentIds: string[]
  ): Promise<void> {
    try {
      const batch = writeBatch(db);

      // 모든 루틴 과제 조회
      const routineAssignmentStatusesRef = collection(db, 'users', userId, 'classes', classId, 'routineAssignmentStatuses');
      const routineAssignmentStatusesSnapshot = await getDocs(routineAssignmentStatusesRef);

      for (const routineAssignmentDoc of routineAssignmentStatusesSnapshot.docs) {
        const routineAssignmentId = routineAssignmentDoc.id;
        
        // 상위 문서는 더 이상 assignedStudentIds를 관리하지 않음
        // DateAssignment-X 문서에서만 관리됨

        // 루틴 과제 상태 참조
        const routineStatusRef = doc(
          db,
          'users',
          userId,
          'classes',
          classId,
          'routineAssignmentStatuses',
          routineAssignmentId
        );

        const datesRef = collection(routineStatusRef, 'dates');
        const datesSnapshot = await getDocs(datesRef);

        // 각 날짜별 멤버 데이터 삭제
        for (const dateDoc of datesSnapshot.docs) {
          // 각 삭제할 멤버의 상태 데이터 삭제
          for (const studentId of studentIds) {
            const studentStatusRef = doc(collection(dateDoc.ref, 'students'), studentId);
            const studentStatusSnapshot = await getDoc(studentStatusRef);

            if (studentStatusSnapshot.exists()) {
              batch.delete(studentStatusRef);
            }
          }
        }
      }

      // 배치 커밋
      await batch.commit();
    } catch (error) {
      console.error('멤버 루틴 과제 데이터 정리 실패:', error);
      throw error;
    }
  },

  /**
   * 최적화된 루틴 과제 멤버 상태 업데이트 (새로운 배열 기반 구조)
   */
  async updateRoutineAssignmentStatusOptimized(
    userId: string,
    classId: string,
    routineId: string,
    dateId: string,
    studentId: string,
    status: AssignmentStatusValue,
    earnedPoints: number = 0,
    memo: string = '',
    memberGroup?: string
  ): Promise<void> {
    try {
      await optimizedAssignmentServiceV3.updateRoutineAssignmentDateStudentStatus(
        userId,
        classId,
        routineId,
        dateId,
        studentId,
        status,
        earnedPoints,
        memo,
        memberGroup
      );
      console.log(`✅ 최적화된 루틴 과제 멤버 상태 업데이트 완료: ${routineId}-${dateId} - ${studentId} - ${status}`);
    } catch (error) {
      console.error('❌ 최적화된 루틴 과제 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  },

  /**
   * 루틴 과제: 날짜 단위 멤버 상태·메모 일괄 저장 (과제체크 모달 등)
   */
  async batchUpdateRoutineAssignmentStatusOptimized(
    userId: string,
    classId: string,
    routineId: string,
    dateId: string,
    studentUpdates: Array<{
      studentId: string;
      status: AssignmentStatusValue;
      earnedPoints: number;
      memo?: string;
    }>,
    memberGroup?: string,
    options?: { fillMissingAssignedAsPending?: boolean; assignedStudentIds?: string[] }
  ): Promise<void> {
    await optimizedAssignmentServiceV3.batchUpdateRoutineAssignmentDateStudentStatuses(
      userId,
      classId,
      routineId,
      dateId,
      studentUpdates,
      memberGroup,
      options
    );
  }
}; 